import { InlineNotification } from '@liveauctioneers/hammer-ui-core/inlineNotification';
import { MonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback } from 'react';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogCurrency,
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
    getRegistrationBidLimit,
    getRegistrationDeclined,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

type Props = {
    catalogId: number;
    className?: string;
};

export const CatalogRegistrationMessagesExperiment = ({ catalogId, className }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const limit = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const bidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));

    const showSendSellerMessageModalDeclined = useCallback(() => {
        const messageDetails = {
            catalogId,
            message: String(formatMessage(['message_registration_declined'])),
            title,
        };

        const subject = `${String(formatMessage(['registration_declined']))}: ${title}`;

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogId, dispatch, formatMessage, sellerName, sellerId, title]);

    if (declined) {
        return (
            <InlineNotification
                className={cn(
                    'w-full items-start border border-border-critical bg-background-critical-light p-xs',
                    className
                )}
                level="critical"
                message={formatMessage(['registration', 'declinedWitLinkExperiment'], {
                    values: {
                        link: (
                            <MonochromeLink
                                size="sm"
                                className="hover:text-text-critical-darker text-text-critical"
                                key="contact-auctioneer"
                                onClick={showSendSellerMessageModalDeclined}
                            >
                                {formatMessage(['registration', 'contactAuctioneerExperiment'])}
                            </MonochromeLink>
                        ),
                    },
                })}
                small
            />
        );
    }

    if (limit) {
        return (
            <InlineNotification
                className={cn(
                    'w-full items-start border border-border-success bg-background-success-light p-xs',
                    className
                )}
                level="success"
                message={formatMessage(['approvedToBidLimitExperiment'], {
                    values: {
                        limit: (
                            <FormattedCurrency
                                key="bid-limit"
                                convert
                                noFraction
                                sourceCurrency={currency}
                                value={bidLimit}
                            />
                        ),
                    },
                })}
                small
            />
        );
    }

    if (approved) {
        return (
            <InlineNotification
                className={cn(
                    'w-full items-start border border-border-success bg-background-success-light p-xs',
                    className
                )}
                level="success"
                message={String(formatMessage(['approvedToBidExperiment']))}
                small
            />
        );
    }

    if (pending) {
        return (
            <InlineNotification
                className={cn('w-full items-start border border-border-info bg-background-info-light p-xs', className)}
                level="info"
                message={String(formatMessage(['registration', 'pendingMessage']))}
                small
            />
        );
    }

    return null;
};
